"use client";

import { useState, useEffect } from "react";

const ADMIN_STATUS_CACHE_KEY = "vocly_admin_status";
const ADMIN_STATUS_CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

interface AdminStatusCache {
  isAdmin: boolean;
  timestamp: number;
}

/**
 * Get cached admin status from sessionStorage
 */
export function getCachedAdminStatus(): boolean | null {
  if (typeof window === "undefined") return null;
  
  try {
    const cached = sessionStorage.getItem(ADMIN_STATUS_CACHE_KEY);
    if (!cached) return null;
    
    const parsed: AdminStatusCache = JSON.parse(cached);
    const age = Date.now() - parsed.timestamp;
    
    // If cache is expired, clear it and return null
    if (age > ADMIN_STATUS_CACHE_EXPIRY_MS) {
      sessionStorage.removeItem(ADMIN_STATUS_CACHE_KEY);
      return null;
    }
    
    return parsed.isAdmin;
  } catch {
    // Invalid cache data, clear it
    sessionStorage.removeItem(ADMIN_STATUS_CACHE_KEY);
    return null;
  }
}

/**
 * Cache admin status in sessionStorage
 */
export function setCachedAdminStatus(isAdmin: boolean): void {
  if (typeof window === "undefined") return;
  
  try {
    const cache: AdminStatusCache = {
      isAdmin,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(ADMIN_STATUS_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // sessionStorage unavailable or quota exceeded - ignore
  }
}

/**
 * Clear cached admin status (call when admin logs in/out)
 */
export function clearAdminStatusCache(): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(ADMIN_STATUS_CACHE_KEY);
}

/**
 * Hook to check if the current user is in admin mode
 * 
 * When admin is logged in, user-level actions should be hidden
 * (ellipsis menus for deleting own posts, post creation)
 * Admin must log out to return to user mode
 * 
 * Uses sessionStorage caching to avoid unnecessary API calls:
 * - Checks cache first, returns cached result if available and not expired
 * - Only makes API call if cache is missing or expired
 * - Cache expires after 5 minutes
 * 
 * @returns {{ isAdmin: boolean, loading: boolean }}
 */
export function useAdminStatus() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      // Check cache first
      const cached = getCachedAdminStatus();
      if (cached !== null) {
        setIsAdmin(cached);
        setLoading(false);
        return;
      }

      // Cache miss - check admin status from server
      try {
        const response = await fetch("/api/admin/check", {
          method: "GET",
          credentials: "include", // Include cookies
        });

        if (response.ok) {
          const data = await response.json();
          const isAdminResult = data.authenticated === true;
          setIsAdmin(isAdminResult);
          setCachedAdminStatus(isAdminResult);
        } else {
          setIsAdmin(false);
          setCachedAdminStatus(false);
        }
      } catch {
        // Network error or other issue - assume not admin
        setIsAdmin(false);
        setCachedAdminStatus(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  return { isAdmin, loading };
}
